import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import Input from 'components/input'
import Modal from 'components/modal'
import api from 'helpers/api'
import { useProject } from 'hooks/useProject'
import { useApplicationStore } from 'hooks/application'
import { AI, Process, Spreadsheet } from 'components/icons'
import Button from 'components/button'
import { PROJECT } from 'app-constants'
import { DOCUMENT_BASE, TABLE_BASE } from 'routes'

interface IResourceMatch {
  text: string
  link: string
  type: string
}

const Search: React.FC = () => {
  const { project, search, openSearch, closeSearch } = useProject()
  const { displayErrorMessage, launchAgentChat } = useApplicationStore()

  const appRootElement = document.getElementById('root')
  const [searchText, setSearchText] = useState<string>('')
  const [results, setResults] = useState<IResourceMatch[] | undefined>()
  const [loadingResults, setLoadingResults] = useState<boolean>(false)

  const [selectedType, setSelectedType] = useState<string>('')

  const handleResourcesSearch = () => {
    setLoadingResults(true)
    api
      .searchProjectResources({
        projectId: project.publicId,
        search: searchText,
        processPublicId: search.processPublicId
      })
      .then((response) => {
        setResults(response.data)
        setLoadingResults(false)
      })
      .catch((error) => {
        displayErrorMessage(error)
        setLoadingResults(false)
        setResults(undefined)
      })
  }

  const types = [
    {
      name: 'All',
      id: ''
    },
    { name: 'Tables', id: 'table' },
    { name: 'Table Views', id: 'view' },
    { name: 'Documents (Name + Description)', id: 'process' },
    { name: 'Document Sections (Name + Description)', id: 'section' }
  ]

  const filteredResults = results
    ? results.filter((result) => {
        if (selectedType === '') return true
        else return result.type === selectedType
      })
    : undefined

  const closeAndResetModal = () => {
    closeSearch()
    setSearchText('')
    setResults(undefined)
    setSelectedType('')
  }

  const handleAiSearch = () => {
    const initialInput = searchText.trim()
    if (initialInput === '') return

    launchAgentChat({
      initialInput,
      documentContextId: search.processPublicId,
      hubContextId: project.publicId,
      reset: true,
      autoSubmit: true
    })

    closeAndResetModal()
  }

  if (appRootElement && search.open) {
    return ReactDOM.createPortal(
      <Modal
        id="search-modal"
        open={search.open}
        setOpen={(open: boolean) => {
          if (open) {
            openSearch()
          } else {
            closeAndResetModal()
          }
        }}
      >
        <div className="text-lg grow-0 shrink-0">
          <div className="mb-5">
            {search.processName
              ? "We will limit the search to the '" + search.processName + "' document."
              : `We will search across the entire ${PROJECT}.`}
          </div>

          <Input
            className="mb-2.5"
            type="text"
            placeholder={'Ask a question or enter a search term...'}
            value={searchText}
            disabled={loadingResults}
            onChange={(event) => {
              setResults(undefined)
              setSearchText(event.target.value)
            }}
          />

          <div className="flex justify-end">
            <Button
              internalType="grey"
              className="h-auto text-base button-shadow-hover p-2.5 w-[200px]"
              onClick={() => handleResourcesSearch()}
              disabled={loadingResults || searchText === ''}
              isLoading={loadingResults}
            >
              Search For Resources
            </Button>

            <Button
              internalType="gradient"
              className="h-auto text-base p-2.5 ml-2.5 w-[200px]"
              onClick={() => handleAiSearch()}
              disabled={searchText.trim() === ''}
            >
              <AI className="mr-[5px]" />
              Ask Morta AI
            </Button>
          </div>
        </div>

        {!search.processPublicId && filteredResults && !loadingResults && (
          <div>
            <div className="font-bold mt-5 mb-5">Relevant {PROJECT} Resources</div>
            <div className="flex items-center pb-2.5">
              {types.map((type, index) => {
                const selected = type.id === selectedType
                return (
                  <div
                    key={index}
                    className={`${selected ? 'bg-surface-strong' : 'hover:bg-surface-sunken'} cursor-pointer rounded py-0.5 px-2.5`}
                    onClick={() => setSelectedType(type.id)}
                  >
                    {type.name}
                  </div>
                )
              })}
            </div>
            <ul className="w-full overflow-auto-hidden overflow-y-auto border-t border-divider p-2 z-[2] h-[400px]">
              {filteredResults.length === 0 && <span>No Results</span>}
              {filteredResults.length > 0 &&
                filteredResults.map((result, resultNumber) => {
                  return (
                    <a
                      key={resultNumber}
                      href={result.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between relative hover:bg-surface-sunken p-2.5"
                    >
                      {result.link.includes(DOCUMENT_BASE) && <Process />}
                      {result.link.includes(TABLE_BASE) && <Spreadsheet />}

                      <div className="w-full flex items-center overflow-hidden truncate ml-2.5 mr-2.5 h-15">
                        {result.text}
                      </div>
                      <div>
                        <svg
                          className="SVGInline-svg SVGInline--cleaned-svg SVG-svg Icon-svg Icon--external-svg SVG--color-svg SVG--color--gray50-svg w-3 h-3"
                          height="16"
                          viewBox="0 0 16 16"
                          width="16"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2 4v10h10v-3a1 1 0 0 1 2 0v4a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2zm5.707 5.707a1 1 0 1 1-1.414-1.414l6.3-6.298H9.017a.998.998 0 1 1 0-1.995h5.986A.995.995 0 0 1 16 .998v5.986a.998.998 0 1 1-1.995 0V3.406z"
                            fillRule="evenodd"
                          ></path>
                        </svg>
                      </div>
                    </a>
                  )
                })}
            </ul>
          </div>
        )}
      </Modal>,
      appRootElement
    )
  } else {
    return <div></div>
  }
}

export default Search
